/**
 * Vuex 4 root store for the SPA.
 *
 * - Auth token + user/accounts persist via vuex-persistedstate key "webwalker"
 *   encrypted with secure-ls (VUE_APP_LS_ENCRYPTION_SECRET).
 * - Empty auth/account fields use `false` (not null) — callers check truthiness.
 * - Plan getters feed router meta.requireApiIntegrations / owner.
 */
import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import SecureLS from 'secure-ls';
import AuthApi from '@/api/AuthApi';
import SettingsApi from '@/api/SettingsApi';
import { mapBillingApiErrors } from '@/helpers/billingErrors';

// AES-encrypted localStorage. Decrypt failure clears the SecureLS blob then
// retries — secret rotation / corrupt state forces a silent wipe + re-login.
const ls = new SecureLS({
  encodingType: 'aes',
  isCompression: false,
  encryptionSecret: process.env.VUE_APP_LS_ENCRYPTION_SECRET,
});

/** Billing returns implicit Free/active with id null when no platform row exists. */
function isPersistedPlatform(platform) {
  return !!(platform && platform.id != null);
}

const store = createStore({
  plugins: [
    createPersistedState({
      key: "webwalker",
      storage: {
        getItem: (key) => {
          try {
            return ls.get(key)
          }
          catch (err) {
            ls.clear()
            return ls.get(key)
          }
        },
        setItem: (key, value) => ls.set(key, value),
        removeItem: (key) => ls.remove(key),
      },
    })
  ],
  // --- state ---
  state: {
    token: false, // Core API token (false = logged out)
    user: false, // Current user details
    accounts: false, // List of accounts
    account: false, // Current account; this probably should be moved to getters (should be URL based)
    isDarkMode: false, // Dark\Light theme
    reverseMessageOrder: false, // Reverse message order in conversations
    conversationFilterType: '', // The last selected type filter in conversations
    conversationFilterReply: false, // The last selected reply-only filter in conversations
    expandTaskCards: false, // Expand task cards in task boards
    isMaintenance: false, // Maintenance mode
    isLoadingRoute: false, // Route loading
    errors: [], // List of errors
  },
  // --- getters ---
  getters: {
    authenticated(state) {
      // Authenticated ≡ truthy token only (no JWT expiry / user presence check).
      if (state.token) {
        return true;
      }
      return false;
    },
    subscription(state) {
      // Get subscription for the current account
      if (state.account &&
          state.account.subscription) {
        return state.account.subscription;
      }
      return null;
    },
    isOwner(state) {
      // Is the owner of the current account?
      if (state.account &&
          state.account.owner &&
          state.user) {
        return (state.account.owner.id == state.user.id);
      }
      return false;
    },
    isActive(state, getters) {
      // Is the current account active?
      // Legacy: match Core Subscription::isActive() (active flag + unexpired expires_at).
      if (getters.subscription && getters.subscription.active) {
        const expiresAt = getters.subscription.expires_at;
        if (!expiresAt || new Date(expiresAt).getTime() > Date.now()) {
          return true;
        }
      }
      // Platform trial (including when an inactive legacy row remains).
      // Ignore Billing's implicit Free/active (id null) — not a real platform row.
      const platform = state.account && state.account.platform_subscription;
      if (isPersistedPlatform(platform) && platform.status === 'trialing' && platform.current_period_end_at) {
        return new Date(platform.current_period_end_at).getTime() > Date.now();
      }
      // Free / Pro / Max after trial, or paid past_due grace.
      if (isPersistedPlatform(platform) && (platform.status === 'active' || platform.status === 'past_due')) {
        return true;
      }
      return false;
    },
    isTrial(state, getters) {
      // Is the current account on free trial?
      // Prefer persisted platform so leftover legacy.trial does not override Free/Pro.
      const platform = state.account && state.account.platform_subscription;
      if (isPersistedPlatform(platform) && platform.status === 'trialing') {
        return true;
      }
      if (isPersistedPlatform(platform) && (platform.status === 'active' || platform.status === 'past_due')) {
        return false;
      }
      if (getters.subscription && getters.subscription.trial) {
        return true;
      }
      return false;
    },
    trialDaysLeft(state, getters) {
      // How many days left in the free trial?
      const platform = state.account && state.account.platform_subscription;
      if (isPersistedPlatform(platform) && platform.status === 'trialing' && platform.current_period_end_at) {
        var platformDays = Math.ceil(((new Date(platform.current_period_end_at)).getTime() - (new Date()).getTime())/(24*60*60*1000));
        if (platformDays <= 0) {
          platformDays = 0;
        }
        return platformDays;
      }
      if (isPersistedPlatform(platform) && (platform.status === 'active' || platform.status === 'past_due')) {
        return null;
      }
      if (getters.subscription && getters.subscription.trial && getters.subscription.expires_at) {
        var days = Math.ceil(((new Date(getters.subscription.expires_at)).getTime() - (new Date()).getTime())/(24*60*60*1000));
        if (days <= 0) {
          days = 0;
        }
        return days;
      }
      return null;
    },
    isBasic(state, getters) {
      // Is the current account on Basic plan?
      // Defaults to true when subscription data is missing. Router meta.requireRegular
      // then treats the account as Basic and can over-redirect to /billing.
      if (getters.subscription) {
        var planType = getters.subscription.plan_type;
        if (planType != 'basic') {
          return false;
        }
      }
      return true; // default to yes since it's the default plan
    },
    isRegular(state, getters) {
      // Is the current account on Regular plan?
      // Defaults to false when subscription is missing (unlike isBasic → true).
      if (getters.subscription) {
        var planType = getters.subscription.plan_type;
        if (planType == 'regular') {
          return true;
        }
      }
      return false;
    },
    isPremium(state, getters) {
      // Is the current account on Premium plan?
      // Defaults to false when subscription is missing (unlike isBasic → true).
      if (getters.subscription) {
        var planType = getters.subscription.plan_type;
        if (planType == 'premium') {
          return true;
        }
      }
      return false;
    },
    canUseApiIntegrations(state) {
      // Core AccountResource.features.api_integrations_enabled (Pro/Max or legacy Regular/Premium).
      if (state.account && state.account.features
          && state.account.features.api_integrations_enabled) {
        return true;
      }
      return false;
    },
  },
  // --- mutations ---
  mutations: {
    setToken(state, token) {
      state.token = token;
    },
    setUser(state, user) {
      state.user = user;
    },
    setAccounts(state, accounts) {
      state.accounts = accounts;
    },
    setAccount(state, account) {
      state.account = account;
    },
    setDarkMode(state, isDarkMode) {
      state.isDarkMode = isDarkMode;
    },
    setReverseMessageOrder(state, reverseMessageOrder) {
      state.reverseMessageOrder = reverseMessageOrder;
    },
    setConversationFilterType(state, conversationFilterType) {
      // Cleared filter is stored as false for settings API; keep UI state as ''.
      state.conversationFilterType = conversationFilterType || '';
    },
    setConversationFilterReply(state, conversationFilterReply) {
      state.conversationFilterReply = conversationFilterReply;
    },
    setExpandTaskCards(state, expandTaskCards) {
      state.expandTaskCards = expandTaskCards;
    },
    setLoadingRoute(state, isLoadingRoute) {
      state.isLoadingRoute = isLoadingRoute;
    },
    setMaintenance(state, isMaintenance) {
      state.isMaintenance = isMaintenance;
    },
    pushErrors(state, errors) {
      // Cap toast stack: 3 on desktop, 1 when viewport width < 960.
      state.errors.push(mapBillingApiErrors(errors) || errors);
      if ((state.errors.length > 3) || (
        (typeof window !== 'undefined' && window.innerWidth < 960)
        &&
        (state.errors.length > 1)
      )) {
        state.errors.shift();
      }
    },
    clearErrors(state) {
      state.errors = [];
    },
  },
  // --- actions ---
  actions: {
    loadUser({ commit, state }) {
      return AuthApi.auth().then(response => {
        // Load user details
        commit('setUser', response.data.data);

        // Load accounts
        if (response.data.data.accounts) {
          commit('setAccounts', response.data.data.accounts);

          // Refresh current account: if none selected yet, every matching pass
          // commits so the last account in the list wins; if selected, only the
          // matching id is refreshed (loose == for string/number ids).
          if (response.data.data.accounts.length) {
            response.data.data.accounts.forEach((account) => {
              if (!state.account || (state.account.id == account.id)) {
                commit('setAccount', account);
              }
            });
          } else {
            commit('setAccount', false);
          }
        }

        // Hydrate settings from Core. conversation_filter_reply is NOT restored
        // here — it lives on persisted state + updateConversationFilterReply only.
        if (response.data.data.settings) {
          commit('setDarkMode', response.data.data.settings.dark_mode);
          commit('setReverseMessageOrder', response.data.data.settings.reverse_message_order || false);
          commit('setConversationFilterType', response.data.data.settings.conversation_filter_type || false);
          commit('setExpandTaskCards', response.data.data.settings.expand_task_cards || false);
        }

        // Initialize Chatwoot
        if (process.env.VUE_APP_CHATWOOT_TOKEN &&
            window.$chatwoot) {
          // Prepare the contact attributes to push them to the chatwoot
          var attributes = {
            user_id: response.data.data.id,
            user_created_at: response.data.data.created_at,
          };

          // Include account & plan data when available
          if (state.account) {
            attributes['account_id'] = state.account.id;
            attributes['account_name'] = state.account.name;
            attributes['account_created_at'] = state.account.created_at;
            if (state.account.subscription) {
              attributes['account_plan_type'] = state.account.subscription.plan_type;
              attributes['account_plan_instances'] = state.account.subscription.units;
              attributes['account_plan_trial'] = state.account.subscription.trial;
              attributes['account_plan_expires_at'] = state.account.subscription.expires_at;
            }
          }

          window.$chatwoot.setCustomAttributes(attributes);

          // Setup user data in the chatwoot
          window.$chatwoot.setUser("webwalker-" + response.data.data.id, {
            email: response.data.data.email,
            name: response.data.data.name,
            identifier_hash: response.data.data.chatwoot_identifier,
          });
        }

        // Disable maintenance mode if enabled
        if (state.isMaintenance) {
          commit('setMaintenance', false);
          window.location.reload();
        }
      })
      .catch((error) => {
        // Swallow: this action does not rethrow. Callers must not assume rejection
        // (unit tests encode that). On non-503 HTTP errors we clear storage; 503
        // toggles maintenance. Transport failures (no response) leave storage alone.
        const response = error && error.response;
        if (response && response.data && response.data.errors) {
          // commit('pushErrors', response.data.errors); // Uncomment to push "Unauthenticated." errors.
        }
        if (response && response.status == 503) {
          // Switch into maintenance mode
          if (!state.isMaintenance) {
            commit('setMaintenance', true);
            window.location.reload();
          }
        } else if (response) {
          // Clears entire origin localStorage (not only the "webwalker" key).
          localStorage.clear();
        }
      });
    },
    switchDarkMode({ commit }, isDarkMode) {
      // Optimistic: PATCH fire-and-forget, then commit local state immediately.
      SettingsApi.updateUser({
        settings: {'dark_mode': isDarkMode},
      });

      // Switch the dark mode on\off
      commit('setDarkMode', isDarkMode);
    },
    switchReverseMessageOrder({ commit }, reverseMessageOrder) {
      // Optimistic: PATCH fire-and-forget, then commit local state immediately.
      SettingsApi.updateUser({
        settings: {'reverse_message_order': reverseMessageOrder},
      });

      // Switch the reverse message order on\off
      commit('setReverseMessageOrder', reverseMessageOrder);
    },
    updateConversationFilterType({ commit }, conversationFilterType) {
      // Optimistic: PATCH fire-and-forget, then commit local state immediately.
      SettingsApi.updateUser({
        settings: {'conversation_filter_type': conversationFilterType},
      });

      // Update the filter value
      commit('setConversationFilterType', conversationFilterType);
    },
    updateConversationFilterReply({ commit }, conversationFilterReply) {
      // Optimistic: PATCH fire-and-forget, then commit local state immediately.
      SettingsApi.updateUser({
        settings: {'conversation_filter_reply': conversationFilterReply},
      });

      // Update the filter value
      commit('setConversationFilterReply', conversationFilterReply);
    },
    switchExpandTaskCards({ commit }, expandTaskCards) {
      // Optimistic: PATCH fire-and-forget, then commit local state immediately.
      SettingsApi.updateUser({
        settings: {'expand_task_cards': expandTaskCards},
      });
      // Switch the expand task cards on\off
      commit('setExpandTaskCards', expandTaskCards);
    },
    loadingRoute({ commit }, isLoadingRoute) {
      // Switch the route loading on\off
      commit('setLoadingRoute', isLoadingRoute);
    },
    pushErrors({ commit }, errors) {
      // Add an error to the list of errors
      commit('pushErrors', errors);
    },
    clearErrors({ commit, state }) {
      // Clear the list of errors
      if (state.errors.length) {
        commit('clearErrors');
      }
    },
  },
});

export default store;
